import { FormEvent, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { SESSION_TIMEOUT_NOTICE_KEY } from '../auth/sessionTimeout'
import { hasSupabaseConfig } from '../lib/supabase'
import '../login.css'
import '../login-christmas-assets.css'
import { useLoginTheme } from '../hooks/useLoginTheme'
import { LoginChristmasDecor, LoginChristmasPanelDecor, LoginChristmasHolly } from '../components/LoginChristmasDecor'

const AGUAS_GUARIROBA_LOGO = '/assets/aguas-guariroba-logo.png'

export function Login() {
  const { signIn } = useAuth()
  const christmasActive = useLoginTheme()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [sessionNotice] = useState(() => {
    const notice = localStorage.getItem(SESSION_TIMEOUT_NOTICE_KEY) ?? ''
    if (notice) localStorage.removeItem(SESSION_TIMEOUT_NOTICE_KEY)
    return notice
  })

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError('')
    setLoading(true)
    try {
      await signIn(email.trim(), password)
      const next = new URL(window.location.href)
      next.searchParams.set('postLogin', '1')
      window.location.replace(next.toString())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível entrar no Suite.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className={`suite-login-shell ${christmasActive ? 'login-theme-christmas' : ''}`} aria-label="Acesso à Suite de Procedimentos Águas Guariroba">
      <section className="suite-login-brand-panel">
        {christmasActive && <LoginChristmasDecor />}
        <div className="suite-login-brand-top">
          <img className="suite-login-logo" src={AGUAS_GUARIROBA_LOGO} alt="Águas Guariroba" />
        </div>

        <div className="suite-login-brand-content">
          <div className="suite-login-eyebrow"><span />Central de Atendimento</div>
          <h1>Informação certa para um atendimento mais simples.</h1>
          <p className="suite-login-brand-copy">Um ambiente único para consultar procedimentos, padronizar orientações e apoiar a operação com rapidez, clareza e segurança.</p>

          <div className="suite-login-feature-grid" aria-label="Recursos da Suite">
            <div className="suite-login-feature">
              <div className="suite-login-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"/></svg>
              </div>
              <strong>Procedimentos</strong>
              <small>Conteúdo organizado para consulta operacional.</small>
            </div>

            <div className="suite-login-feature">
              <div className="suite-login-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              </div>
              <strong>Consulta rápida</strong>
              <small>Menos navegação, mais foco na resolução.</small>
            </div>

            <div className="suite-login-feature">
              <div className="suite-login-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/><path d="m9 12 2 2 4-4"/></svg>
              </div>
              <strong>Acesso controlado</strong>
              <small>Ambiente interno com autenticação corporativa.</small>
            </div>
          </div>
        </div>

        <div className="suite-login-brand-bottom">
          <span><strong>Águas Guariroba</strong> · Suite de Procedimentos</span>
          <span>Ambiente de uso interno</span>
        </div>
      </section>

      <section className="suite-login-panel">
        {christmasActive && <LoginChristmasPanelDecor />}
        <div className="suite-login-top">
          {christmasActive && <img className="suite-login-mobile-logo" src={AGUAS_GUARIROBA_LOGO} alt="Águas Guariroba" />}
          <div className="suite-login-secure-badge">
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/><path d="m9 12 2 2 4-4"/></svg>
            Acesso corporativo
          </div>
        </div>

        <div className="suite-login-card">
          {christmasActive && <LoginChristmasHolly />}
          <h2>Bem-vindo à Suite{christmasActive && (
            <span className="login-christmas-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="8" width="18" height="13" rx="1.5" />
                <path d="M3 8h18M12 8v13M12 8H8a3 3 0 0 1-3-3c0-3 3-3.5 5-1.5L12 8Zm0 0h4a3 3 0 0 0 3-3c0-3-3-3.5-5-1.5L12 8Z" />
              </svg>
              Edição de Natal
            </span>
          )}</h2>
          <p className="suite-login-sub">Entre com suas credenciais para acessar o ambiente de procedimentos da Central de Atendimento.</p>

          {!hasSupabaseConfig && <div className="suite-login-notice" role="alert">Variáveis do Supabase ainda não configuradas neste ambiente.</div>}
          {sessionNotice && <div className="suite-login-notice" role="status" aria-live="polite">{sessionNotice}</div>}

          <form className="suite-login-form" onSubmit={handleSubmit}>
            <div className="suite-login-field">
              <label className="suite-login-field-label" htmlFor="suite-login-email">E-mail corporativo</label>
              <div className="suite-login-input-wrap">
                <svg className="suite-login-input-icon" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
                <input
                  id="suite-login-email"
                  type="email"
                  autoComplete="username"
                  placeholder="nome.sobrenome@aguasguariroba.com.br"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  disabled={!hasSupabaseConfig || loading}
                />
              </div>
            </div>

            <div className="suite-login-field">
              <label className="suite-login-field-label" htmlFor="suite-login-password">Senha</label>
              <div className="suite-login-input-wrap">
                <svg className="suite-login-input-icon" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                <input
                  id="suite-login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  disabled={!hasSupabaseConfig || loading}
                />
                <button
                  className="suite-login-toggle-eye"
                  type="button"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  onClick={() => setShowPassword((current) => !current)}
                  disabled={!hasSupabaseConfig || loading}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 3l18 18"/><path d="M10.6 10.7a2 2 0 0 0 2.7 2.7"/><path d="M9.9 5.1A11 11 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-2.1 3.1M6.2 6.2C3.7 7.9 2 12 2 12s3.5 7 10 7a10.6 10.6 0 0 0 4.1-.8"/></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="2.7"/></svg>
                  )}
                </button>
              </div>
            </div>

            {error && <div className="suite-login-notice" role="alert" aria-live="polite">{error}</div>}

            <button className="suite-login-submit" type="submit" disabled={!hasSupabaseConfig || loading}>
              {loading ? 'Entrando...' : 'Entrar na Suite'}
              {!loading && <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>}
            </button>
          </form>

          <div className="suite-login-access-note">
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 10v6M12 7h.01"/></svg>
            <div>
              <strong>Acesso restrito</strong>
              <span>Utilize somente a conta autorizada para sua operação. Em caso de bloqueio, acione a liderança responsável.</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
